/**
 * @module app.shared
 */

import { Logger, Appender } from 'aurelia-logging'
import { LogProcessor } from './log-processor';
import { LogItem, EssetteLogLevel, ILogItem } from './log-item';
import * as Core from 'app/core/models/essette-api';
import { Indexed } from 'app/shared/lib/types';

// style settings
const tagStyle = 'font-weight:bold;';
const tagStyles: Indexed<string> = {
    error: 'background:red;color:white;' + tagStyle,
    warn: 'background:orange;color:black;' + tagStyle,
    info: 'background:#42b0f0;color:black;' + tagStyle,
    debug: 'background:darkgray;color:black' + tagStyle
};

const messageStyle = 'font-weight:light;';
const messageStyles: Indexed<string> = {
    error: 'color:red;' + messageStyle,
    warn: 'color:orange;' + messageStyle,
    info: 'color:#42b0f0;' + messageStyle,
    debug: 'color:gray;' + messageStyle
};

const extraStyle = 'color:gray;font-style:italic;font-weight:light;'
const extraStyles: Indexed<string> = {
    error: extraStyle,
    warn: extraStyle,
    info: extraStyle,
    debug: extraStyle
};

const detailsStyle = 'color:black;'
const detailsStyles: Indexed<string> = {
    error: detailsStyle,
    warn: detailsStyle,
    info: detailsStyle,
    debug: detailsStyle
};

function formatLog(level: EssetteLogLevel, message: string, style: string = '', source: string = '', extra: string = '', details?: any): void {
    const text = `%c %s %c %s` +
        (extra ? ` %c%s` : '') +
        (details ? ' %c%o' : '');

    const args = [
        text,
        tagStyles[level],
        source,
        messageStyles[level] + style,
        message,
        ...(extra ? [extraStyles[level], extra] : []),
        ...(details ? [detailsStyles[level], details] : [])];

    console[level](...args);
}

function formatTime(dateTime: Date): string {
    const hh = `${dateTime.getHours()}`;
    const mm = `${dateTime.getMinutes()}`;
    const ss = `${dateTime.getSeconds()}`;
    const parts: string[] = [];
    if(hh.length < 2) { parts.push('0'); }; parts.push(hh);
    parts.push(':');
    if(mm.length < 2) { parts.push('0'); }; parts.push(mm);
    parts.push(':');
    if(ss.length < 2) { parts.push('0'); }; parts.push(ss);
    return parts.join('');
}

export class EssetteLogAppender implements Appender {

    private previousLogItems: LogItem[] = [];

    public constructor(
        private readonly processor: LogProcessor) { }

    public debug(logger: Logger, ...rest: any[]): void {
        this.processLogItem(logger, 'debug', rest);
    }

    public info(logger: Logger, ...rest: any[]): void {
        this.processLogItem(logger, 'info', rest);
    }

    public warn(logger: Logger, ...rest: any[]): void {
        this.processLogItem(logger, 'warn', rest);
    }

    public error(logger: Logger, ...rest: any[]): void {
        this.processLogItem(logger, 'error', rest);
    }

    private createErrorLogItem(logger: Logger, error: Error, level: EssetteLogLevel): LogItem {
        const item = {
            level: level,
            target: 'all',
            error: error
        } as Partial<ILogItem>;
        return LogItem.create(item, { source: logger.id });
    }

    private processLogItem(logger: Logger, level: EssetteLogLevel, parameters: any[]): void {
        const error = (parameters.find(item => item instanceof Error) || null) as Error | null;
        let logItem = (parameters.find(item => item instanceof LogItem) || null) as LogItem | null;

        if(!logItem && error) {
            logItem = this.createErrorLogItem(logger, error, level);
        }

        if(logItem) {
            let target = logItem.target;
            if(target === 'default') {
                target = logItem.level === 'debug' ? 'console' : 'all';
            }
            switch(target) {
                case 'console':
                    this.sendToConsole(logger, logItem);
                    break;
                case 'server':
                    this.sendToServer(logItem);
                    break;
                case 'all':
                    this.sendToConsole(logger, logItem);
                    this.sendToServer(logItem);
                    break;
            }
        }
        else {
            this.defaultLog(logger, level, parameters);
        }
    }

    private defaultLog(logger: Logger, level: EssetteLogLevel, parameters: any[]): void {
        if(!console) { return; }

        formatLog(level, `${parameters[0]}`, '', logger.id, '(default)', parameters.length > 1 ? parameters.slice(1) : undefined);
    }

    private sendToConsole(logger: Logger, item: LogItem): void {
        if(!console) { return; }

        // track duplicates
        if(this.previousLogItems.length > 0 && item.isDuplicate(this.previousLogItems[0])) {
            // push to previous array and don't bother actually logging it
            this.previousLogItems.push(item);
            return;
        }

        // start new list of previous log items
        this.previousLogItems = [item];

        const extra = item.trace || formatTime(new Date(item.timestamp)) || undefined;
        const style = item.style || '';
        const args = item.error ? [this.previousLogItems, item.error] : [this.previousLogItems];

        formatLog(item.level, item.message, style, logger.id, extra, ...args);

        const table: any = item.details && item.details.table || undefined;
        if(table) {
            console.table(table, item.details.columns || undefined);
        }

    }

    private sendToServer(item: LogItem): void {
        let entry: Core.LogEntry = {
            originTimestamp: item.timestamp,
            message: item.message,
            level: item.level,
            source: item.source,
            url: item.url,
            error: null
        }
        this.processor.process(entry, item.priority === 'high');
    }

}
